import { renderAsync } from 'docx-preview';
import { showToast } from '../utils/toast.js';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const DOCX_EXTS  = ['docx', 'doc'];
const PDF_EXTS   = ['pdf'];

function getExt(filename) {
    return (filename || '').split('.').pop().toLowerCase();
}

/**
 * FilePreview — opens any file in a modal.
 *   DOCX/DOC → rendered with docx-preview
 *   PDF       → <iframe>
 *   Images    → <img>
 *   Other     → download prompt
 *
 * Attach data-docx-preview-url and data-docx-preview-name to any button/link.
 */
export class DocxPreview {
    constructor() {
        this.modal     = null;
        this.container = null;
        this.isOpen    = false;
    }

    init() {
        this.modal     = document.getElementById('docx_preview_modal');
        this.container = document.getElementById('docx-preview-container');

        if (!this.modal || !this.container) return this;

        document.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-docx-preview-url]');
            if (!btn) return;
            e.preventDefault();
            this.open(
                btn.dataset.docxPreviewUrl,
                btn.dataset.docxPreviewName || 'Document'
            );
        });

        this.modal.querySelector('.docx-preview-close')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.close();
        });
        this.modal.querySelector('.docx-preview-overlay')?.addEventListener('click', () => {
            this.close();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) this.close();
        });

        return this;
    }

    async open(url, name) {
        if (!this.modal) return;

        const ext = getExt(name);

        // Show modal with spinner immediately
        this.container.innerHTML = '<div class="docx-preview-spinner-wrap"><span class="delete-member-spinner"></span></div>';
        document.getElementById('docx-preview-title').textContent = name;
        this.modal.style.display = 'flex';
        requestAnimationFrame(() => this.modal.classList.add('docx-preview-modal--visible'));
        this.isOpen = true;
        document.body.style.overflow = 'hidden';

        // Update panel class for file type layout
        const panel = this.modal.querySelector('.docx-preview-panel');
        panel.dataset.previewType = DOCX_EXTS.includes(ext) ? 'docx'
            : PDF_EXTS.includes(ext)   ? 'pdf'
            : IMAGE_EXTS.includes(ext) ? 'image'
            : 'other';

        if (DOCX_EXTS.includes(ext)) {
            await this.renderDocx(url);
        } else if (PDF_EXTS.includes(ext)) {
            this.renderPdf(url);
        } else if (IMAGE_EXTS.includes(ext)) {
            this.renderImage(url, name);
        } else {
            this.renderUnsupported(url, name);
        }
    }

    // ── Renderers ────────────────────────────────────────────────────────────

    /** Ensure preview fetches use ?view=1 so the download handler does not log them as downloads. */
    withViewParam(url) {
        return url.includes('view=1') ? url : url + (url.includes('?') ? '&' : '?') + 'view=1';
    }

    async renderDocx(url) {
        try {
            const res = await fetch(this.withViewParam(url), { credentials: 'same-origin' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            this.container.innerHTML = '';
            await renderAsync(blob, this.container, null, {
                className:       'docx-preview-content',
                inWrapper:       true,
                breakPages:      true,
                useBase64URL:    true,
                renderHeaders:   true,
                renderFooters:   true,
                renderFootnotes: true,
                renderEndnotes:  true,
            });
        } catch (err) {
            console.error('DocxPreview error:', err);
            this.container.innerHTML = '';
            showToast('Could not load the document. Please try downloading it instead.', 'error');
            this.close();
        }
    }

    renderPdf(url) {
        this.container.innerHTML = `<iframe src="${this.withViewParam(url)}" class="docx-preview-iframe" title="PDF Preview"></iframe>`;
    }

    renderImage(url, name) {
        const img = document.createElement('img');
        img.className = 'docx-preview-image';
        img.alt = name;
        img.onload  = () => { this.container.innerHTML = ''; this.container.appendChild(img); };
        img.onerror = () => { showToast('Could not load the image.', 'error'); this.close(); };
        img.src = this.withViewParam(url);
    }

    renderUnsupported(url, name) {
        this.container.innerHTML = `
            <div class="docx-preview-unsupported">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D5D7DA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                <p>Preview not available for this file type.</p>
                <a href="${url}" class="button gold" download="${name}">Download file</a>
            </div>`;
    }

    // ── Close ─────────────────────────────────────────────────────────────────

    close() {
        if (!this.modal) return;
        this.modal.classList.remove('docx-preview-modal--visible');
        setTimeout(() => {
            this.modal.style.display = 'none';
            this.container.innerHTML = '';
        }, 200);
        this.isOpen = false;
        document.body.style.overflow = '';
    }
}
